import apiUseCases from "../../api";
import { parseAxiosApiError } from "@src/cloud-connect/utils/parse-axios-api-error";
import store from "@src/reducers/store";
import { CreateSkillRequestBody } from "@src/types/skill-types";
import { queryGatewayOperationStatusUseCase as querySkillOperationStatusUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-gateway-operation-status-use-case";
import {
  OPERATION_WARNING_STATES,
  getOperationState,
  resolveOperationStatus,
} from "@src/cloud-connect/utils/resolve-operation-status";
import {
  CreateSkillApiResponse,
  CreateSkillApiResponseItem,
} from "@src/cloud-connect/api/client-configuration/post-create-skill";
import {
  GatewayOperationError,
  GetGatewayOperationStatusResponse,
} from "@src/types/gateway-onboarding";
import {
  OPERATION_POLL_MAX_ATTEMPTS,
  extractGatewayOperationError,
  resolveOperationErrorDetails,
  waitForNextOperationPoll,
} from "@src/cloud-connect/utils/onboarding-operation";

interface FailedSkillResult {
  type: string;
  error: string;
  details?: GatewayOperationError;
}

export interface CreateSkillResult {
  success: boolean;
  isWarning?: boolean;
  successfulSkillTypes?: string[];
  failedSkillTypes?: FailedSkillResult[];
  operationIdsBySkillType?: Partial<Record<string, string>>;
  operationResultsBySkillType?: Partial<
    Record<string, GetGatewayOperationStatusResponse>
  >;
  error?: string;
  errorDetails?: GatewayOperationError;
}

const normalizeCreateSkillResponse = (
  response: CreateSkillApiResponse,
): CreateSkillApiResponseItem[] =>
  Array.isArray(response) ? response : [response];

const pollOperation = async (
  operationId: string,
  pendingMessage: string,
  signal?: AbortSignal,
) => {
  let lastState = "";
  let lastOperation: GetGatewayOperationStatusResponse | undefined;

  for (let attempt = 0; attempt < OPERATION_POLL_MAX_ATTEMPTS; attempt++) {
    const pollResult = await querySkillOperationStatusUseCase(
      operationId,
      signal,
    );

    if (!pollResult.success) {
      return {
        status: "error" as const,
        error: pollResult.error || "Installation failed.",
        operation: lastOperation,
        details: resolveOperationErrorDetails(
          lastOperation,
          pollResult.errorData,
        ),
      };
    }

    lastOperation = pollResult.data;
    lastState = getOperationState(pollResult.data);
    const operationStatus = resolveOperationStatus(pollResult.data);

    if (operationStatus === "success") {
      return { status: "success" as const, operation: pollResult.data };
    }

    if (operationStatus === "error") {
      return {
        status: "error" as const,
        error:
          pollResult.data?.detail ||
          pollResult.data?.message ||
          pollResult.error ||
          "Installation failed.",
        operation: pollResult.data,
        details: resolveOperationErrorDetails(pollResult.data),
      };
    }

    if (attempt < OPERATION_POLL_MAX_ATTEMPTS - 1) {
      await waitForNextOperationPoll(signal);
    }
  }

  return {
    status: "warning" as const,
    error: pendingMessage,
    isWarning: OPERATION_WARNING_STATES.includes(lastState),
    operation: lastOperation,
    details: resolveOperationErrorDetails(lastOperation),
  };
};

export const postCreateSkillUseCase = async (
  bodies: CreateSkillRequestBody[],
  machineId?: string,
  pendingMessage = "Installation is still in progress.",
  signal?: AbortSignal,
): Promise<CreateSkillResult> => {
  try {
    const namespace = store.getState().yourSlice.authContext?.customerNamespace;
    if (!namespace) {
      return {
        success: false,
        error: "Namespace not available.",
      };
    }

    const resolvedMachineId =
      machineId || store.getState().yourSlice.connectionView.source.source_id;

    if (!resolvedMachineId) {
      return {
        success: false,
        error: "Machine not selected.",
      };
    }

    const createResponse = await apiUseCases.postCreateSkill(
      namespace,
      resolvedMachineId,
      { skills: bodies },
    );

    const operationItems = normalizeCreateSkillResponse(createResponse.data);

    if (operationItems.length === 0) {
      return {
        success: false,
        error: "Skill installation did not return any operations.",
      };
    }

    const successfulSkillTypes: string[] = [];
    const failedSkillTypes: FailedSkillResult[] = [];
    const operationIdsBySkillType: Partial<Record<string, string>> = {};
    const operationResultsBySkillType: Partial<
      Record<string, GetGatewayOperationStatusResponse>
    > = {};
    let hasWarning = false;

    for (const skillBody of bodies) {
      const operationItem = operationItems.find(
        (item) => item.skill_type === skillBody.type,
      );

      if (!operationItem) {
        failedSkillTypes.push({
          type: skillBody.type,
          error: "Installation failed.",
        });
        continue;
      }

      if (!operationItem.operation_id) {
        successfulSkillTypes.push(skillBody.type);
        continue;
      }

      operationIdsBySkillType[skillBody.type] = operationItem.operation_id;

      const pollResult = await pollOperation(
        operationItem.operation_id,
        pendingMessage,
        signal,
      );

      if (pollResult.operation) {
        operationResultsBySkillType[skillBody.type] = pollResult.operation;
      }

      if (pollResult.status === "success") {
        successfulSkillTypes.push(skillBody.type);
        continue;
      }

      if (pollResult.status === "warning") {
        hasWarning = true;
      }

      failedSkillTypes.push({
        type: skillBody.type,
        error: pollResult.error,
        details: pollResult.details,
      });
    }

    return {
      success: true,
      isWarning: hasWarning,
      successfulSkillTypes: Array.from(new Set(successfulSkillTypes)),
      failedSkillTypes,
      operationIdsBySkillType,
      operationResultsBySkillType,
    };
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }

    const parsedError = parseAxiosApiError(error, "Installation failed.");

    return {
      success: false,
      error: parsedError.message,
      errorDetails: extractGatewayOperationError(parsedError.data),
    };
  }
};
